/**
 * Single-hue sequential ramp for the attribution funnel, derived from the
 * brand indigo token. Funnel stages are one ordered magnitude (an ordinal
 * scale), so each stage takes a lightness step of the same hue — light→dark
 * down the funnel on light surfaces, flipped (dark→light) on dark surfaces
 * where lightness reads as intensity.
 *
 * Anchor constants are validated with the dataviz ordinal-ramp checks
 * (monotone L, adjacent ΔL ≥ 0.06 up to 6 steps, light end ≥ 2:1 on the
 * surface, single hue) in both modes; keep them in that envelope when tuning.
 */
import { useMemo } from 'react'
import { readCssVar } from './nivoTheme'

export interface RampAnchors {
  from: { l: number; c: number }
  to: { l: number; c: number }
}

export const LIGHT_RAMP_ANCHORS: RampAnchors = { from: { l: 0.78, c: 0.1 }, to: { l: 0.44, c: 0.23 } }
export const DARK_RAMP_ANCHORS: RampAnchors = { from: { l: 0.48, c: 0.2 }, to: { l: 0.8, c: 0.1 } }

/** Indigo hue near --color-brand-500 (273.01), used when the token can't be
 * resolved (SSR / tests). */
export const FALLBACK_HUE = 275

const OKLCH_RE = /^oklch\(\s*([\d.]+)(%?)\s+([\d.]+)\s+([\d.]+)(?:deg)?\s*\)$/

/** Parse an `oklch(L C H)` string (as stored in basis tokens). Accepts `%` on
 * lightness and `deg` on hue; returns null for anything else. */
export function parseOklch(value: string): { l: number; c: number; h: number } | null {
  const m = OKLCH_RE.exec(value.trim())
  if (!m) return null
  const l = m[2] === '%' ? parseFloat(m[1]) / 100 : parseFloat(m[1])
  return { l, c: parseFloat(m[3]), h: parseFloat(m[4]) }
}

const round = (v: number, places: number) => {
  const f = 10 ** places
  return Math.round(v * f) / f
}

/** Interpolate n lightness/chroma steps between the anchors at a constant hue.
 * n === 1 takes the intense (`to`) anchor so a lone stage reads as the brand. */
export function buildFunnelRamp(n: number, anchors: RampAnchors, hue: number): string[] {
  if (n <= 0) return []
  const { from, to } = anchors
  return Array.from({ length: n }, (_, i) => {
    const t = n === 1 ? 1 : i / (n - 1)
    const l = round(from.l + (to.l - from.l) * t, 4)
    const c = round(from.c + (to.c - from.c) * t, 4)
    return `oklch(${l} ${c} ${round(hue, 2)})`
  })
}

/** Resolve the ramp for the active mode: hue from --color-brand-500, anchor
 * set by the surface (--background lightness < 0.5 → dark). Falls back to the
 * light anchors + brand hue when tokens are unresolvable. */
export function getFunnelRamp(n: number): string[] {
  const hue = parseOklch(readCssVar('--color-brand-500'))?.h ?? FALLBACK_HUE
  const surfaceL = parseOklch(readCssVar('--background'))?.l
  const anchors = surfaceL != null && surfaceL < 0.5 ? DARK_RAMP_ANCHORS : LIGHT_RAMP_ANCHORS
  return buildFunnelRamp(n, anchors, hue)
}

/** Ramp for the active mode, resolved fresh on every render — after a
 * light/dark flip the funnel picks up the flipped anchors on its next render.
 * Referentially stable while values are unchanged (same contract as
 * useChartColors). */
export function useFunnelRamp(n: number): string[] {
  const key = getFunnelRamp(n).join('|')
  return useMemo(() => (key === '' ? [] : key.split('|')), [key])
}
